import { T } from 'src/shared/helpers';
import {
    PropertiesPanelLeafConfig,
    PropertiesPanelPropertyConfig
} from 'src/components/pages/productConfiguration/steps/configurator-components/scene-properties-panel';
import { FinishMode, PBR_RANGE_LIMITS, pbrSections, PbrSection } from './pbrMaterialLayout';
import { textureSlot } from './textureSlot';

export interface PbrPanelSection {
    id: string;
    label: string;
    groups: PropertiesPanelPropertyConfig[];
}

// SI INDICIZZANO LE FOGLIE, NON I GRUPPI. Il pannello vecchio annida: dentro il gruppo `metallic`
// c'è una proprietà che si chiama anch'essa `metallic`, e dentro `clear-coat-roughness` (gruppo)
// una `clear-coat-roughness` (valore). Indicizzando anche i contenitori, chiedere una riga
// restituirebbe a volte la riga e a volte tutto il ramo che la contiene — cioè un pannello che
// cambia forma a seconda di dove capita un nome. I contenitori si attraversano e basta: qui
// dentro esistono solo le foglie, e i loro nomi sono quelli che la tabella nomina.
const indexLeaves = (properties: PropertiesPanelPropertyConfig[], into: Map<string, PropertiesPanelLeafConfig[]>) => {
    properties.forEach((property) => {
        if (!property) return;
        if (property.type === 'group') {
            indexLeaves(property.properties, into);
            return;
        }
        const found = into.get(property.id);
        if (found) found.push(property);
        else into.set(property.id, [property]);
    });
    return into;
};

// I limiti corretti si applicano alla riga senza toccare chi l'ha costruita: si riscrive la
// copia che finisce nel pannello, non l'originale. `sliderMax` arriva insieme agli altri, e il
// controllo sa già cosa farne — cursore fin lì, campo numerico libero.
const withLimits = (property: PropertiesPanelLeafConfig): PropertiesPanelLeafConfig => {
    const limits = property.type === 'range' ? PBR_RANGE_LIMITS[property.id] : undefined;
    return limits ? { ...property, ...limits } : property;
};

// GLI ID CHE UNO SLOT DI TEXTURE PRODUCE. `createTexturePropertyConfig` ne emette fino a quattro
// con lo stesso prefisso — la mappa, la sua versione mobile, la scala, il canale UV — e sono la
// seconda metà di un canale: il file che lo dipinge, non il valore che governa. È questa
// distinzione a mettere il divisore nel cassetto, e a dire quale anteprima va sulla riga.
const isMapPart = (id: string) => id.endsWith('-image') || id.endsWith('-texture-scaling') || id.endsWith('-uv-channel');

// LE DUE INVERSIONI DELLA NORMALE SONO UNA RIGA SOLA, «Invert · X Y», come nel mockup (utente,
// 2026-09-15). Il generatore le costruisce come due spunte con la frase intera: si fondono qui,
// nel posto dove stava la prima.
const INVERT_LETTERS = [
    { id: 'invert-normal-map-X', axis: 'x', letter: 'X' },
    { id: 'invert-normal-map-Y', axis: 'y', letter: 'Y' }
] as const;

const withInvertLetters = (properties: PropertiesPanelLeafConfig[]): PropertiesPanelLeafConfig[] => {
    const ids: string[] = INVERT_LETTERS.map((entry) => entry.id);
    const at = properties.findIndex((property) => ids.includes(property.id));
    if (at === -1) return properties;

    const toggles = INVERT_LETTERS.flatMap((entry) => {
        const box = properties.find((property) => property.id === entry.id);
        if (box?.type !== 'checkbox') return [];
        return [
            {
                id: entry.id,
                axis: entry.axis,
                letter: entry.letter,
                pressed: box.checked,
                ariaLabel: entry.axis === 'x' ? T._('Invert X', 'Admin') : T._('Invert Y', 'Admin'),
                onChange: box.onChange,
                disabled: box.disabled
            }
        ];
    });

    const rest = properties.filter((property) => !ids.includes(property.id));
    rest.splice(at, 0, {
        type: 'axis-toggles',
        id: 'invert-normal-map',
        label: T._('Invert', 'Admin'),
        toggles,
        disabled: toggles.every((toggle) => toggle.disabled)
    });
    return rest;
};

// SCALATURA E CANALE UV DIVENTANO IL MAPPING, dove chi compone il pannello lo sa costruire: la
// scalatura prende il posto di tutte e due, il canale UV se ne va — sta dentro il mapping. Dove non
// lo sa, perché una cube map non ha un piano, restano le righe del pannello vecchio. Le due righe
// sparite insieme (lo slot vuoto del pacchetto) non chiedono niente, e il mapping non compare.
// LA TENDINA DIVENTA PILLOLA: stesse voci, stesso gestore. La pillola parla di stringhe, la tendina di opzioni.
const asPill = (property: PropertiesPanelLeafConfig): PropertiesPanelLeafConfig | undefined => {
    if (property.type !== 'select') return undefined;
    const options = property.options.map((option: { value: unknown; label: unknown }) => ({ value: String(option.value), label: String(option.label) }));
    return {
        type: 'segmented',
        id: property.id,
        options,
        value: String(property.value?.value ?? ''),
        disabled: property.disabled,
        ariaLabel: typeof property.label === 'string' ? property.label : undefined,
        onChange: (value: string) => property.onChange?.(property.options.find((option: { value: unknown }) => String(option.value) === value) ?? null)
    };
};

const withMapping = (property: PropertiesPanelLeafConfig, mapping?: (type: string) => PropertiesPanelLeafConfig | null): PropertiesPanelLeafConfig[] => {
    const suffix = ['-texture-scaling', '-uv-channel'].find((end) => property.id.endsWith(end));
    const built = suffix && mapping ? mapping(property.id.slice(0, -suffix.length)) : null;
    if (!built) return [property];
    return suffix === '-texture-scaling' ? [built] : [];
};

/**
 * Rimonta le proprietà di un materiale PBR nella gerarchia di `PBR_SECTIONS`.
 *
 * Riceve l'elenco piatto che `useMaterialsPropertiesCreator` costruisce già, più le poche righe
 * che quel pannello non ha (`buildPbrExtraProperties`), e restituisce le sezioni. Ciò che la
 * tabella non nomina non finisce nel risultato, ed è esattamente così che si nasconde.
 */
export const buildPbrMaterialPanel = (
    flat: PropertiesPanelPropertyConfig[],
    extra: PropertiesPanelPropertyConfig[] = [],
    options: {
        finish?: FinishMode;
        switcher?: PropertiesPanelLeafConfig;
        /**
         * UNA RIGA DEL GENERATORE, RISCRITTA PER QUI. Si sostituisce la copia che finisce in questo
         * pannello, per id, e la riga del generatore resta com'è: la leggono anche altre viste.
         * Serve dove qui il gesto giusto è un altro — vedi la spunta dell'occlusione del pacchetto.
         *
         * Tornando `null` la riga non si disegna: serve dove il pannello vecchio costruisce una riga
         * che qui non ha senso — la scalatura di una mappa che questa vista non mostra.
         */
        overrides?: Record<string, (property: PropertiesPanelLeafConfig) => PropertiesPanelLeafConfig | null>;
        /** Il mapping della texture di un canale, costruito da chi ha il materiale: vedi `withMapping`. */
        mapping?: (type: string) => PropertiesPanelLeafConfig | null;
    } = {}
): PbrPanelSection[] => {
    const leaves = indexLeaves([...flat, ...extra], new Map());
    const sections = pbrSections(options.finish ?? 'orm');

    // UNO STESSO NOME PUÒ VALERE PER PIÙ RIGHE, e va bene: uno slot di texture emette la mappa e
    // la sua versione mobile con lo stesso id. Si prendono tutte, nell'ordine in cui il pannello
    // le costruisce — desktop e poi mobile — che è l'ordine in cui si leggono.
    const override = (property: PropertiesPanelLeafConfig) => (options.overrides?.[property.id] ? options.overrides[property.id](property) : property);
    const take = (ids: string[]) =>
        ids.flatMap((id) =>
            (leaves.get(id) ?? [])
                .map(withLimits)
                .map(override)
                .filter((property): property is PropertiesPanelLeafConfig => property != null)
        );

    return sections
        .map((section: PbrSection) => ({
            id: section.id,
            label: section.label,
            groups: section.groups
                .map((group) => {
                    // L'INTERRUTTORE DELLA FAMIGLIA STA NELLA TESTATA, non fra le righe. È ciò che decide se
                    // il resto vale: in mezzo agli altri parametri sarebbe una riga come le altre in un
                    // elenco che invece governa, e per spegnere uno strato bisognerebbe prima aprirlo.
                    const [feature] = group.feature ? take([group.feature]) : [];
                    const [pillSource] = group.pill ? take([group.pill]) : [];

                    // NELLA TESTATA CI STA UNA COSA SOLA, e nessun gruppo ne chiede due: `feature` accende una
                    // famiglia, la pillola sceglie un modo, il commutatore cambia la forma della card.
                    const trailing = feature ?? (pillSource && asPill(pillSource)) ?? (group.switcher ? options.switcher : undefined);

                    const channels = group.channels
                        .map((channel) => {
                            const properties = take(channel.take);
                            if (properties.length === 0) return null;
                            // UN CANALE SENZA NOME NON È UN CANALE: è il corpo del gruppo. Lo usano i
                            // tre gruppi di Rendering, che non hanno cose da toccare ma una manciata
                            // di valori sciolti — e un titolo che ripete quello del gruppo sarebbe una
                            // maniglia in più per arrivare alla stessa riga.
                            if (!channel.label) return properties;

                            // UN CANALE È SEMPRE UNA RIGA. Quello che cambia da canale a canale è che cosa
                            // resta IN VISTA — un valore, la sola casella della mappa, o niente — mai la
                            // forma: quattro canali disegnati come accordion in mezzo a righe erano due
                            // grammatiche nello stesso pannello (utente, 2026-09-14), e in un pannello di
                            // materiale — che di roba ne ha tanta — la forma si legge prima del nome.
                            //
                            // Il resto si divide da sé in due: i valori che il canale governa e il file che
                            // lo dipinge. È questa divisione a disegnare il divisore nel cassetto.
                            const rows = withInvertLetters(properties);
                            const head = channel.onRow && channel.onRow !== 'map' ? rows.find((property) => property.id === channel.onRow) : undefined;
                            const values = rows.filter((property) => property !== head && !isMapPart(property.id));

                            // LA MAPPA DIVENTA UNO SLOT, e la prima è quella desktop: il pannello vecchio le
                            // emette nell'ordine in cui si leggono — la mappa, poi la sua versione mobile.
                            // L'anteprima sulla riga si prende prima della conversione, dall'indirizzo.
                            const images = rows.filter(
                                (property): property is Extract<PropertiesPanelLeafConfig, { type: 'image' }> => property.type === 'image'
                            );
                            const map = rows
                                .filter((property) => isMapPart(property.id))
                                .flatMap((property) =>
                                    property.type === 'image' ? [textureSlot(property, property !== images[0])] : withMapping(property, options.mapping)
                                );

                            return [
                                {
                                    type: 'channel' as const,
                                    id: group.id + '-' + channel.label,
                                    label: channel.label,
                                    defaultIsOpen: channel.open,
                                    head,
                                    properties: values.length ? values : undefined,
                                    map: map.length ? map : undefined,
                                    preview: images[0]?.images?.[0]?.src || undefined
                                }
                            ];
                        })
                        .filter(Boolean)
                        .flat() as PropertiesPanelPropertyConfig[];

                    // IL COMMUTATORE TIENE IN PIEDI LA CARD anche quando dentro non c’è niente: è da lì
                    // che si torna indietro, e una card sparita sarebbe una modalità senza uscita.
                    if (!trailing && channels.length === 0) return null;

                    return {
                        type: 'group' as const,
                        id: group.id,
                        header: group.label,
                        defaultIsOpen: Boolean(group.open),
                        trailing,
                        properties: channels
                    };
                })
                .filter(Boolean) as PropertiesPanelPropertyConfig[]
        }))
        .filter((section) => section.groups.length > 0);
};

// LA RICERCA SCARTA, NON EVIDENZIA. Un pannello di materiale sono più di cento righe divise in
// quattro capitoli, e la domanda di chi scrive nella barra è «dov'è la ruvidità»: si tolgono le
// righe che non rispondono e resta il PERCORSO — capitolo, card, canale — così ciò che si trova
// si legge nel posto dove vive, e la volta dopo si sa dove cercarlo. Evidenziare dentro un
// pannello lungo il doppio dello schermo vuol dire far scorrere per trovare l'evidenziato.
//
// NIENTE SI TOCCA: questa funzione restituisce un altro elenco, e a campo vuoto è il pannello
// di prima, intero.
const says = (text: unknown, needle: string) => typeof text === 'string' && text.toLowerCase().includes(needle);

/** Il nome PROPRIO di una riga o di un contenitore — non quello di ciò che contiene. */
const named = (row: any, needle: string) => says(row?.label, needle) || says(row?.header, needle);

/** Risponde se lo dice lei o se lo dice qualcosa che porta dentro: la testa, i valori, la mappa. */
const answers = (row: any, needle: string): boolean =>
    !!row && (named(row, needle) || [row.head, ...(row.properties ?? []), ...(row.map ?? [])].some((child: any) => answers(child, needle)));

// APERTO, PERCHÉ È UNA RISPOSTA. Una riga che risponde e resta chiusa si è trovata due volte.
// `defaultIsOpen` vale al montaggio, ed è per questo che chi disegna il pannello lo rimonta a
// ogni ricerca (vedi MaterialOverlaySimple).
const opened = (row: PropertiesPanelPropertyConfig) => (row.type === 'group' || row.type === 'channel' ? { ...row, defaultIsOpen: true } : row);

/**
 * Il pannello ridotto a ciò che risponde. `query` vuota: le sezioni tali e quali.
 *
 * IL NOME DI UN CONTENITORE VALE QUANTO QUELLO DI UNA RIGA: chi scrive «finish» cerca la card, e
 * se risponde la card dentro resta tutto — nascondere righe dentro il risultato vorrebbe dire
 * rispondere e poi coprire la risposta.
 */
export const filterPbrMaterialPanel = (sections: PbrPanelSection[], query: string): PbrPanelSection[] => {
    const needle = query.trim().toLowerCase();
    if (!needle) return sections;

    return sections
        .map((section) => {
            if (says(section.label, needle)) return section;

            const groups = section.groups
                .map((group) => {
                    if (named(group, needle)) return opened(group);
                    if (group.type !== 'group') return answers(group, needle) ? opened(group) : null;

                    const properties = group.properties.filter((child) => answers(child, needle)).map(opened);
                    return properties.length ? { ...group, properties, defaultIsOpen: true } : null;
                })
                .filter(Boolean) as PropertiesPanelPropertyConfig[];

            return groups.length ? { ...section, groups } : null;
        })
        .filter(Boolean) as PbrPanelSection[];
};
